// Shared access to the Eventtia Connect v3 API.
//
// Every request goes through a private "cache lane": a query param no other
// client builds. The reason is a CORS/CDN interaction that breaks plain fetches:
// the Rails app only emits `Access-Control-Allow-Origin` when the request
// carries an `Origin` header, while the CDN in front of connect.eventtia.com
// caches the response (`max-age` 300-600s, `public`) without `Vary: Origin` and
// without `Origin` in the cache key. Whichever variant lands in the cache first
// is served to everyone for the rest of the window, so a copy stored by a
// non-browser request (an SSR portal, a monitor, a crawler, curl) is replayed to
// browsers with no CORS header and the fetch fails.
//
// Because the lane param is only ever used by this portal, its cache entry can
// only be populated by a browser — which always sends `Origin` — so the cached
// copy always carries the CORS header. The lane value is shared by every visitor
// on purpose and its window matches the endpoints' own `max-age`, which keeps
// origin traffic identical to what the CDN would serve anyway.
//
// The proper fix belongs in the API (`Vary: Origin`, `Origin` in the CDN cache
// key, or always emitting `Access-Control-Allow-Origin`). Drop this module's
// lane once that lands.

const API_BASE = 'https://connect.eventtia.com/en/api/v3';
const LANE_WINDOW_MS = 5 * 60 * 1000;
// Namespaces the lane so it is not a bare timestamp another client could land on.
const LANE_PREFIX = 'sgp';
// The attendee_types endpoint forces `page[size]=24`; this bounds the follow-up
// requests for events with an unusual number of types.
const MAX_ATTENDEE_TYPE_PAGES = 10;

const laneUrl = (url, lane) => {
	const separator = url.includes('?') ? '&' : '?';
	const bucket = Math.floor(Date.now() / LANE_WINDOW_MS);
	return `${url}${separator}_portal_cb=${LANE_PREFIX}${bucket}${lane}`;
};

const requestLane = async (url, lane) => {
	// A CORS failure (including a poisoned cache entry) and an offline browser both
	// reject with a TypeError and leave no response to inspect.
	const response = await fetch(laneUrl(url, lane));
	if (!response.ok) throw new Error(`${url} responded ${response.status}`);
	return response.json();
};

/**
 * GET a Connect v3 path and return the parsed body, throwing on failure.
 * Retries once on the fallback lane when the request never reached a response,
 * which is what a poisoned cache entry looks like from the browser. An HTTP
 * error status is not retried: the origin did answer, so a second call would
 * only add load to a server that is already failing. Both lanes are
 * deterministic and shared, so the fallback stays cacheable instead of forcing a
 * fresh origin hit for every card and every visitor.
 * @param {string} path Path below /api/v3, e.g. `/events/uri-1234/attendee_types`
 */
export const fetchFromConnect = async (path) => {
	const url = `${API_BASE}${path}`;
	try {
		return await requestLane(url, '');
	} catch (error) {
		if (error.name !== 'TypeError') throw error;
		return requestLane(url, 'r');
	}
};

/**
 * Every attendee type of an event, following pagination.
 * @param {string} eventUri
 * @returns {Promise<Array<object>>} JSON:API attendee_type resources
 */
export const fetchAttendeeTypes = async (eventUri) => {
	const path = `/events/${eventUri}/attendee_types`;
	const firstPage = await fetchFromConnect(path);
	const attendeeTypes = Array.isArray(firstPage?.data) ? [...firstPage.data] : [];

	const totalPages = firstPage?.meta?.total_pages || 1;
	const lastPage = Math.min(totalPages, MAX_ATTENDEE_TYPE_PAGES);
	if (totalPages > lastPage)
		console.warn(`fetchAttendeeTypes: ${eventUri} has ${totalPages} pages of attendee types, reading ${lastPage}`);

	for (let page = 2; page <= lastPage; page += 1) {
		// eslint-disable-next-line no-await-in-loop
		const nextPage = await fetchFromConnect(`${path}?page[number]=${page}`);
		if (Array.isArray(nextPage?.data)) attendeeTypes.push(...nextPage.data);
	}

	return attendeeTypes;
};
